import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { type CommandCenterHeadline, opsApi, agentsApi, type RecentAgentAction } from "../lib/api";
import { AgentAvatar } from "../components/AgentAvatar";

export function CommandCenterPage() {
  const q = useQuery({
    queryKey: ["command-center"],
    queryFn: opsApi.commandCenter,
    refetchInterval: 60_000,
  });

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-3xl font-display font-bold">Command Center</h1>
        <p className="text-ink-600 mt-1">
          Cross-portfolio health at a glance. Auto-refreshes every minute.
        </p>
      </header>

      {q.isLoading && <div className="bg-white rounded-xl border border-ink-100 p-8 text-center text-ink-500">Loading…</div>}
      {q.error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-4">
          Failed: {String((q.error as Error).message)}
        </div>
      )}
      {q.data && (
        <>
          <Headline data={q.data.headline} />
          <Alerts data={q.data.headline} />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <TopSitesPanel
              title="Most leads (last 7d)"
              items={q.data.topSitesByLeads}
              valueKey="recentLeads"
              suffix=" leads"
            />
            <TopSitesPanel
              title="Most impressions (last 7d)"
              items={q.data.topSitesByImpressions}
              valueKey="impressions"
              suffix=" imp"
            />
          </div>
          <ActionPanel data={q.data.headline} />
          <AgentActivityFeed />
        </>
      )}
    </div>
  );
}

function AgentActivityFeed() {
  const q = useQuery({
    queryKey: ["agent-recent-actions"],
    queryFn: () => agentsApi.recentActions(20),
    refetchInterval: 30_000,
  });
  if (q.isLoading || !q.data) return null;
  return (
    <div className="bg-white rounded-xl border border-ink-100 p-5">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-sm font-display font-bold uppercase tracking-wider text-ink-700">
          Agent activity feed
        </h2>
        <Link to="/agents" className="text-xs text-ink-500 hover:text-accent">All agents →</Link>
      </div>
      {q.data.actions.length === 0 ? (
        <p className="text-sm text-ink-500">
          No agent actions yet (heartbeats excluded). Run an agent or wait for the next cron tick.
        </p>
      ) : (
        <ul className="divide-y divide-ink-50">
          {q.data.actions.map((a) => <ActivityRow key={a.id} action={a} />)}
        </ul>
      )}
    </div>
  );
}

function ActivityRow({ action: a }: { action: RecentAgentAction }) {
  const tone: Record<string, string> = {
    ok: "text-green-700 bg-green-50",
    failed: "text-red-700 bg-red-50",
    escalated: "text-amber-800 bg-amber-50",
    skipped: "text-ink-500 bg-ink-50",
    dry_run: "text-blue-700 bg-blue-50",
  };
  const badgeCls = tone[a.outcome] ?? "text-ink-500 bg-ink-50";
  return (
    <li className="py-2 flex items-start gap-3">
      <Link to={`/agents/${a.agentId}`} className="flex-shrink-0">
        <AgentAvatar id={a.agentId} displayName={a.agentDisplayName} emoji={a.agentEmoji} size={32} />
      </Link>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <Link to={`/agents/${a.agentId}`} className="text-sm font-medium text-ink-900 hover:text-accent">
            {a.agentDisplayName}
          </Link>
          <code className="text-xs text-ink-500 font-mono">{a.actionKind}</code>
          <span className={`text-xs px-2 py-0.5 rounded font-medium ${badgeCls}`}>{a.outcome}</span>
          {a.dryRun && <span className="text-xs text-blue-600">[dry]</span>}
          {a.domain && (
            <Link to={`/sites/${a.siteId}`} className="text-xs text-ink-500 hover:text-accent">
              {a.domain}
            </Link>
          )}
        </div>
        {a.errorMessage && (
          <p className="text-xs text-red-700 mt-0.5 line-clamp-2 max-w-prose">{a.errorMessage}</p>
        )}
      </div>
      <time className="text-xs text-ink-500 tabular-nums whitespace-nowrap">
        {relTimeShort(a.startedAt)}
      </time>
    </li>
  );
}

function relTimeShort(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h`;
  return `${Math.round(hr / 24)}d`;
}

function Headline({ data }: { data: CommandCenterHeadline }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
      <Stat label="Sites" value={data.siteCount} hint={`${data.liveSiteCount} live`} />
      <Stat label="Leads (7d)" value={data.totalLeads7d} hint={`${data.highScoreLeads7d} high-score`} accent={data.totalLeads7d > 0} />
      <Stat label="Impressions (7d)" value={data.totalImpressions7d.toLocaleString()} accent={data.totalImpressions7d > 0} />
      <Stat label="Clicks (7d)" value={data.totalClicks7d.toLocaleString()} accent={data.totalClicks7d > 0} />
      <Stat label="Outreach placed" value={data.outreachPlacedCount} hint="live backlinks" />
      <Stat label="Journo queue" value={data.newJournoCount} hint="new pitches" accent={data.newJournoCount > 0} />
    </div>
  );
}

function Alerts({ data }: { data: CommandCenterHeadline }) {
  const items: Array<{ tone: "red" | "amber" | "blue"; label: string; cta?: { label: string; to: string } }> = [];
  if (data.sitesWithAuditFailures > 0) {
    items.push({
      tone: "red",
      label: `${data.sitesWithAuditFailures} site${data.sitesWithAuditFailures === 1 ? "" : "s"} failing audit`,
      cta: { label: "View sites", to: "/sites" },
    });
  }
  if (data.sitesIndexedPartial > 0) {
    items.push({
      tone: "amber",
      label: `${data.sitesIndexedPartial} site${data.sitesIndexedPartial === 1 ? "" : "s"} have un-indexed pages`,
      cta: { label: "View sites", to: "/sites" },
    });
  }
  if (data.sitesNeverAudited > 0) {
    items.push({
      tone: "amber",
      label: `${data.sitesNeverAudited} site${data.sitesNeverAudited === 1 ? "" : "s"} never audited`,
      cta: { label: "Run rankforge audit", to: "/sites" },
    });
  }
  if (data.prDraftsPending > 0) {
    items.push({
      tone: "blue",
      label: `${data.prDraftsPending} press release${data.prDraftsPending === 1 ? "" : "s"} drafted, awaiting submission`,
    });
  }
  if (data.newJournoCount > 0) {
    items.push({
      tone: "blue",
      label: `${data.newJournoCount} new journo querie${data.newJournoCount === 1 ? "" : "s"} to triage`,
      cta: { label: "Open journo", to: "/journo" },
    });
  }

  if (items.length === 0) {
    return (
      <div className="bg-green-50 border border-green-200 text-green-800 rounded-xl p-4 text-sm">
        ✓ No active alerts. Portfolio is healthy.
      </div>
    );
  }

  const colors: Record<string, string> = {
    red: "bg-red-50 border-red-200 text-red-800",
    amber: "bg-accent-100 border-accent-200 text-accent-700",
    blue: "bg-blue-50 border-blue-200 text-blue-800",
  };
  return (
    <div className="space-y-2">
      {items.map((it, i) => (
        <div key={i} className={`flex items-center justify-between rounded-lg border px-4 py-2.5 ${colors[it.tone]}`}>
          <span className="text-sm font-medium">{it.label}</span>
          {it.cta && (
            <Link to={it.cta.to} className="text-xs underline decoration-dotted hover:no-underline">
              {it.cta.label} →
            </Link>
          )}
        </div>
      ))}
    </div>
  );
}

function TopSitesPanel({ title, items, valueKey, suffix }: {
  title: string;
  items: Array<{ id: string; domain: string; recentLeads?: number; impressions?: number }>;
  valueKey: "recentLeads" | "impressions";
  suffix: string;
}) {
  const max = items.reduce((m, it) => Math.max(m, (it[valueKey] ?? 0)), 0) || 1;
  return (
    <div className="bg-white rounded-xl border border-ink-100 p-5 shadow-sm">
      <h2 className="font-semibold mb-3">{title}</h2>
      {items.length === 0 ? (
        <p className="text-ink-500 text-sm">No data yet.</p>
      ) : (
        <ul className="space-y-2">
          {items.map((it) => {
            const v = it[valueKey] ?? 0;
            const pct = (v / max) * 100;
            return (
              <li key={it.id} className="flex items-center gap-3">
                <Link to={`/sites/${it.id}`} className="text-sm font-medium text-ink-900 hover:text-accent flex-1 truncate">
                  {it.domain}
                </Link>
                <div className="w-32 h-2 bg-ink-100 rounded overflow-hidden">
                  <div className="h-full bg-accent" style={{ width: `${pct}%` }}></div>
                </div>
                <span className="text-xs tabular-nums text-ink-600 w-20 text-right">{v.toLocaleString()}{suffix}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function ActionPanel({ data }: { data: CommandCenterHeadline }) {
  return (
    <div className="bg-white rounded-xl border border-ink-100 p-5 shadow-sm">
      <h2 className="font-semibold mb-3">Today's recommended actions</h2>
      <ul className="space-y-2 text-sm">
        {data.highScoreLeads7d > 0 && (
          <Action ok>
            <strong>{data.highScoreLeads7d} high-score lead{data.highScoreLeads7d === 1 ? "" : "s"}</strong> in the last 7 days.
            <Link to="/leads?minScore=50" className="ml-1 underline decoration-dotted">Open leads inbox →</Link>
          </Action>
        )}
        {data.newJournoCount > 0 && (
          <Action>
            Pitch a journo query (highest single off-page lever).
            <Link to="/journo" className="ml-1 underline decoration-dotted">Open journo tab →</Link>
          </Action>
        )}
        {data.prDraftsPending > 0 && (
          <Action>
            Submit {data.prDraftsPending} drafted press release{data.prDraftsPending === 1 ? "" : "s"} to PRWeb / EIN.
          </Action>
        )}
        {data.sitesNeverAudited > 0 && (
          <Action warn>
            Run <code className="bg-ink-100 px-1 rounded text-xs">rankforge audit --site …</code> on the {data.sitesNeverAudited} un-audited site{data.sitesNeverAudited === 1 ? "" : "s"}.
          </Action>
        )}
        {data.totalImpressions7d === 0 && data.siteCount > 0 && (
          <Action>
            No SC impressions yet — Google's pipeline lag is 2-3 days for new sites. After that:
            <code className="bg-ink-100 px-1 rounded text-xs ml-1">rankforge sc-pull --all-sites</code>
          </Action>
        )}
        <Action>
          Review the operator handbook for daily/weekly/monthly workflow:
          <code className="bg-ink-100 px-1 rounded text-xs ml-1">docs/operator-handbook.md</code>
        </Action>
      </ul>
    </div>
  );
}

function Action({ children, ok, warn }: { children: React.ReactNode; ok?: boolean; warn?: boolean }) {
  const tone = ok ? "text-green-800" : warn ? "text-accent-700" : "text-ink-700";
  const bullet = ok ? "✓" : warn ? "⚠" : "→";
  return (
    <li className={`flex gap-2 ${tone}`}>
      <span className="font-bold">{bullet}</span>
      <span className="flex-1">{children}</span>
    </li>
  );
}

function Stat({ label, value, hint, accent }: { label: string; value: number | string; hint?: string; accent?: boolean }) {
  return (
    <div className="bg-white border border-ink-100 rounded-xl p-3">
      <div className="text-xs uppercase tracking-wider text-ink-500 font-medium">{label}</div>
      <div className={`mt-1 text-xl font-display font-bold tabular-nums ${accent ? "text-accent-600" : "text-ink-900"}`}>
        {value}
      </div>
      {hint && <div className="text-xs text-ink-500 mt-0.5">{hint}</div>}
    </div>
  );
}
